import { browser } from '$app/env';
import {
	Collections,
	type BoatsResponse,
	type BookingsResponse,
	type ListingsResponse,
	type PortsResponse,
	type UsersResponse
} from '@/pocketbase/pocketbase-types';
import { pb } from '@/utils/ApiClient';
import { error, redirect } from '@sveltejs/kit';
import type { PageLoad } from './$types';

export const load: PageLoad = async ({ url }) => {
	if (!browser) {
		return { booking: null, error: null };
	}

	const bookingId = url.searchParams.get('id');

	if (!bookingId) {
		throw error(400, 'No se proporcionó ID de reserva');
	}

	try {
		// Verificar que el usuario esté autenticado
		if (!pb.authStore.isValid || !pb.authStore.record) {
			throw redirect(302, '/login');
		}

		// Cargar el booking con todas las relaciones expandidas
		const booking = await pb.collection(Collections.Bookings).getOne<
			BookingsResponse<{
				listing: ListingsResponse<
					string[],
					{
						boat: BoatsResponse<{ port: PortsResponse }>;
						user: UsersResponse;
					}
				>;
				user: UsersResponse;
			}>
		>(bookingId, {
			expand: 'listing,listing.boat,listing.boat.port,listing.user,user'
		});

		// Determinar el rol del usuario: 'guest' (usuario que hizo la reserva) o 'owner' (propietario)
		const role = url.searchParams.get('role') || 'guest';
		const bookingUserId = typeof booking.user === 'string' ? booking.user : booking.user[0];
		const listingOwnerId = booking.expand?.listing?.expand?.user?.id || null;

		// Verificar permisos según el rol
		if (role === 'owner') {
			// Si es propietario, verificar que el usuario actual es el dueño del listing
			if (!listingOwnerId || listingOwnerId !== pb.authStore.record.id) {
				throw error(403, 'No tienes permiso para valorar esta reserva como propietario');
			}
		} else {
			// Si es guest, verificar que el usuario actual es el que hizo la reserva
			if (bookingUserId !== pb.authStore.record.id) {
				throw error(403, 'No tienes permiso para valorar esta reserva');
			}
		}

		// Validar que la fecha de fin ya pasó
		const endDate = new Date(booking.endDate);
		const today = new Date();
		today.setHours(0, 0, 0, 0); // Resetear horas para comparar solo fechas
		endDate.setHours(0, 0, 0, 0);

		if (endDate >= today) {
			throw error(400, 'No puedes valorar esta reserva hasta que haya finalizado');
		}

		// Verificar si ya existe una reseña para esta reserva
		let existingReview = null;
		try {
			existingReview = await pb.collection(Collections.Reviews).getFirstListItem(
				pb.filter('reviewer = {:reviewer} && booking = {:booking}', {
					reviewer: pb.authStore.record?.id,
					booking: bookingId
				})
			);
		} catch {
			// No existe reseña, está bien
		}

		return {
			booking,
			existingReview,
			role,
			error: null
		};
	} catch (err: unknown) {
		console.error('Error al cargar la reserva:', err);

		// Si es un error de SvelteKit (redirect o error), re-lanzarlo
		if (err && typeof err === 'object' && 'status' in err) {
			const status = (err as { status: number }).status;
			if (status === 302 || status >= 400) {
				throw err;
			}
		}

		// Para otros errores
		throw error(500, 'Error al cargar los detalles de la reserva');
	}
};
